import { useState, useEffect, useRef } from 'react';
import ActionModal from '../../../../../components/action-modal';
import { FilterDropdown } from '../../../../../components';
import { Plus, Trash, WebSearchIcon } from '../../../../../assets/images';
import {
  AuthType,
  isOAuthAuthType,
  MCP_AUTH_TYPE_OPTIONS,
  MCPServerConfig,
  MCPServerPayload,
  McpReAuthContext,
  normalizeAuthType,
  TransportType,
  tuneModalProps,
  ValidationErrors,
} from './tune-modal.props';
import { CreateMcpChatService, DiscoverMcpService } from '../../services/chat';
import makeToast from '../../../../../components/toaster';
import { validateTextFieldForSpecialCharsAndNum } from '../../../../../services';
import { validateMcpServerUrl } from '../../lib/mcp-tool-utils';
import {
  HeadersText,
  NameText,
  TokenText,
  TransPortText,
  URLText,
  AuthTypeText,
} from '../data/constants';

const DEFAULT_CONFIG: MCPServerConfig = {
  authType: 'none',
  url: '',
  name: '',
  token: '',
  clientId: '',
  clientSecret: '',
  transportType: 'http',
};

function TuneModal({ onClose, open, reAuth, prefill }: tuneModalProps) {
  const isReconnect = Boolean(reAuth?.url && reAuth?.mcpName) && !prefill?.url && !prefill?.mcpName;
  const [config, setConfig] = useState<MCPServerConfig>(DEFAULT_CONFIG);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [fieldErrors, setFieldErrors] = useState<{ key: string[]; value: string[] }>({
    key: [],
    value: [],
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [popupWindow, setPopupWindow] = useState<Window | null>(null);
  const authSuccessRef = useRef<boolean>(false);
  const [keyValuePairs, setKeyValuePairs] = useState<{ id: string; key: string; value: string }[]>([
    { id: '1', key: '', value: '' },
  ]);

  useEffect(() => {
    let source: McpReAuthContext | null = null;
    if (prefill?.url || prefill?.mcpName) {
      source = prefill ?? null;
    } else if (reAuth?.url || reAuth?.mcpName) {
      source = reAuth ?? null;
    }
    if (!source) {
      return;
    }

    setConfig((prev) => ({
      ...prev,
      url: source.url || '',
      name: source.mcpName || '',
      authType: source.authType ? normalizeAuthType(source.authType) : prev.authType,
      transportType:
        source.transportType === 'http' || source.transportType === 'sse'
          ? source.transportType
          : prev.transportType,
    }));
  }, [prefill, reAuth]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const isSuccessMessage =
        event.data?.message === 'success' ||
        event.data === 'success' ||
        (typeof event.data === 'object' && event.data?.type === 'success') ||
        event.data?.status === 'success' ||
        event.data?.success === true;

      if (isSuccessMessage) {
        authSuccessRef.current = true;
        if (popupWindow && !popupWindow.closed) {
          popupWindow.close();
        }
        if (popupWindow) {
          setPopupWindow(null);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [popupWindow]);

  const handleUrlBlur = async () => {
    if (!config.url) return;
    try {
      const response = await DiscoverMcpService(config.url, config.transportType);
      const discovery = response?.data?.data;
      if (!discovery) return;

      if (discovery.auth_required === false || discovery.auth_type === 'none') {
        setConfig((prev) => ({ ...prev, authType: 'none' }));
      } else if (discovery.auth_type === 'mixed' && Array.isArray(discovery.options)) {
        const preferred = discovery.options.find(
          (option: string) => option === 'oauth_dynamic' || option === 'oauth_pre_registered',
        );
        if (preferred) {
          setConfig((prev) => ({ ...prev, authType: normalizeAuthType(preferred) }));
        }
      } else if (
        discovery.auth_required &&
        discovery.auth_type &&
        discovery.auth_type !== 'unknown'
      ) {
        setConfig((prev) => ({
          ...prev,
          authType: normalizeAuthType(discovery.auth_type),
        }));
      }
    } catch {
      // Discovery is best-effort; user can still pick auth manually.
    }
  };

  const onChange = (field: keyof MCPServerConfig, value: string | AuthType) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    let error = '';
    if (field === 'name' && typeof value === 'string') {
      error = validateTextFieldForSpecialCharsAndNum(value, 'Name');
    } else if (field === 'url' && typeof value === 'string') {
      error = validateMcpServerUrl(value);
    }
    setErrors((prev) => ({ ...prev, [field]: error || undefined }));
  };

  const addKeyValuePair = () => {
    setKeyValuePairs((prev) => [...prev, { id: Date.now().toString(), key: '', value: '' }]);
  };

  const removeKeyValuePair = (index: number) => {
    setKeyValuePairs((prev) => prev.filter((_, i) => i !== index));
  };

  const updateKeyValuePair = (index: number, field: 'key' | 'value', value: string) => {
    const updatedPairs = keyValuePairs.map((pair, i) =>
      i === index ? { ...pair, [field]: value } : pair,
    );
    setKeyValuePairs(updatedPairs);

    if (fieldErrors[field][index]) {
      const newFieldErrors = { ...fieldErrors };
      newFieldErrors[field] = [...newFieldErrors[field]];
      newFieldErrors[field][index] = '';
      setFieldErrors(newFieldErrors);
    }

    const allFieldsFilled = updatedPairs.every((pair) => pair.key && pair.value);
    if (allFieldsFilled && errors.headers) {
      setErrors((prev) => ({ ...prev, headers: undefined }));
    }
  };

  const resetForm = () => {
    setConfig(DEFAULT_CONFIG);
    setErrors({});
    setFieldErrors({ key: [], value: [] });
    setKeyValuePairs([{ id: '1', key: '', value: '' }]);
  };

  const onCloseButton = () => {
    if (!isLoading) {
      resetForm();
      onClose();
    }
  };

  const buildPayload = (): MCPServerPayload => {
    const payload: MCPServerPayload = {
      name: config.name,
      auth_type: config.authType,
      url: config.url,
      transportType: config.transportType,
    };

    if (config.authType === 'bearer') {
      payload.token = config.token;
    }

    if (config.authType === 'headers') {
      payload.headers = keyValuePairs.reduce(
        (acc, pair) => {
          if (pair.key && pair.value) {
            acc[pair.key] = pair.value;
          }
          return acc;
        },
        {} as Record<string, string>,
      );
    }

    if (config.authType === 'oauth_pre_registered') {
      payload.client_id = config.clientId;
      if (config.clientSecret) {
        payload.client_secret = config.clientSecret;
      }
    }

    if (prefill?.mcpId != null) {
      payload.reconnect_mcp_id = prefill.mcpId;
    }

    return payload;
  };

  const handleSave = async () => {
    const nameValidationError = validateTextFieldForSpecialCharsAndNum(config.name, 'Name');
    const urlValidationError = validateMcpServerUrl(config.url);

    let headerValidationError = '';
    const keyErrors: string[] = [];
    const valueErrors: string[] = [];

    if (config.authType === 'headers') {
      let hasEmptyRow = false;
      keyValuePairs?.forEach((pair, index) => {
        if (!pair.key || !pair.value) {
          hasEmptyRow = true;
          keyErrors[index] = !pair.key ? 'Key is required' : '';
          valueErrors[index] = !pair.value ? 'Value is required' : '';
        } else {
          keyErrors[index] = '';
          valueErrors[index] = '';
        }
      });
      if (hasEmptyRow) {
        headerValidationError = 'Please fill all header fields';
      }
      setFieldErrors({ key: keyErrors, value: valueErrors });
    }

    let tokenValidationError = '';
    if (config.authType === 'bearer' && !config.token) {
      tokenValidationError = 'Token is required!';
    }

    let clientIdValidationError = '';
    if (config.authType === 'oauth_pre_registered' && !config.clientId) {
      clientIdValidationError = 'Client ID is required!';
    }

    const newErrors: ValidationErrors = {};
    if (nameValidationError) newErrors.name = nameValidationError;
    if (urlValidationError) newErrors.url = urlValidationError;
    if (headerValidationError) newErrors.headers = headerValidationError;
    if (tokenValidationError) newErrors.token = tokenValidationError;
    if (clientIdValidationError) newErrors.clientId = clientIdValidationError;

    if (Object.keys(newErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }

    const payload = buildPayload();

    try {
      setIsLoading(true);
      CreateMcpChatService(payload)
        .then((response: { status: number; data: { data: { auth_uri: string } } }) => {
          if (response?.status === 200) {
            const authUri = response?.data?.data?.auth_uri;
            if (authUri && isOAuthAuthType(config.authType)) {
              const popup = window.open(
                authUri,
                'MCPAuthPopup',
                'width=500,height=600,top=200,left=200,' +
                  'toolbar=no,menubar=no,location=no,status=no,' +
                  'resizable=no,scrollbars=yes',
              );
              if (popup) {
                popup.focus();
                setPopupWindow(popup);
                authSuccessRef.current = false;
                const checkClosed = setInterval(() => {
                  if (popup.closed) {
                    clearInterval(checkClosed);
                    setTimeout(() => {
                      resetForm();
                      if (authSuccessRef.current) {
                        makeToast('success', 'MCP server connected successfully!');
                      } else {
                        makeToast('info', 'MCP is created unauthorized');
                      }
                      setIsLoading(false);
                      authSuccessRef.current = false;
                      onCloseButton();
                    }, 500);
                  }
                }, 500);
              }
            } else {
              resetForm();
              makeToast('success', 'MCP server connected successfully!');
              setIsLoading(false);
              onCloseButton();
            }
          }
        })
        .catch((error: { status: number; response: { data: { message: string } } }) => {
          if (error.status === 400) {
            makeToast(
              'error',
              error?.response?.data?.message || 'Invalid MCP server configuration',
            );
          } else {
            makeToast('error', error?.response?.data?.message || 'Failed to connect to MCP server');
          }
          setIsLoading(false);
        });
    } catch {
      setIsLoading(false);
    }
  };

  let confirmButtonText = 'Connect';
  if (isLoading) {
    confirmButtonText = 'Connecting...';
  } else if (isReconnect) {
    confirmButtonText = 'Reconnect';
  }

  return (
    <ActionModal
      open={open}
      modalTitle={isReconnect ? 'Reconnect MCP Server' : 'Connect to MCP Server'}
      modalDesc={
        isReconnect
          ? 'Reconnect to your MCP server with a fresh connection'
          : 'Configure your MCP server connection'
      }
      modalLogo={WebSearchIcon}
      resetData={onCloseButton}
      saveData={handleSave}
      confirmButtonText={confirmButtonText}
      disableSaveButton={isLoading}
      className="w-[500px]"
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col text-sm">
          <span className="text-[#344054] font-medium">
            {AuthTypeText}
            <span className="text-red-600">*</span>
          </span>
          <FilterDropdown
            options={MCP_AUTH_TYPE_OPTIONS}
            value={config.authType}
            onChange={(v: string) => onChange('authType', v as AuthType)}
            placeholder="Select auth type"
            showClear={false}
            className="mt-1 border border-[#E4E4E7] rounded-lg w-full px-3 py-2 text-left flex items-center justify-between"
          />
        </div>

        <div className="flex flex-col text-sm">
          <span className="text-[#344054] font-medium">
            {TransPortText}
            <span className="text-red-600">*</span>
          </span>
          <FilterDropdown
            options={[
              { value: 'sse', label: 'SSE' },
              { value: 'http', label: 'HTTP Streamable' },
            ]}
            value={config.transportType}
            onChange={(v: string) => onChange('transportType', v as TransportType)}
            placeholder="Select transport"
            showClear={false}
            className="mt-1 border border-[#E4E4E7] rounded-lg w-full px-3 py-2 text-left flex items-center justify-between"
          />
        </div>

        <div className="flex flex-col text-sm">
          <span className="text-[#344054] font-medium">
            {URLText}
            <span className="text-red-600">*</span>
          </span>
          <input
            placeholder="Enter server URL"
            className={`mt-1 rounded-lg ${
              errors.url ? '!border-red-500 border' : 'border border-[#E4E4E7]'
            }`}
            value={config.url}
            onChange={(e) => onChange('url', e.target.value)}
            onBlur={handleUrlBlur}
          />
          {errors.url && <span className="text-red-500 text-xs mt-1">{errors.url}</span>}
        </div>

        <div className="flex flex-col text-sm">
          <span className="text-[#344054] font-medium">
            {NameText}
            <span className="text-red-600">*</span>
          </span>
          <input
            placeholder="Enter connection name"
            className={`mt-1 rounded-lg ${
              errors.name ? '!border-red-500 border' : 'border border-[#E4E4E7]'
            }`}
            value={config.name}
            onChange={(e) => onChange('name', e.target.value)}
          />
          {errors.name && <span className="text-red-500 text-xs mt-1">{errors.name}</span>}
        </div>

        {config.authType === 'bearer' && (
          <div className="flex flex-col text-sm">
            <span className="text-[#344054] font-medium">
              {TokenText}
              <span className="text-red-600">*</span>
            </span>
            <input
              type="password"
              placeholder="Enter authentication token"
              className={`mt-1 rounded-lg ${
                errors.token ? '!border-red-500 border' : 'border border-[#E4E4E7]'
              }`}
              value={config.token}
              onChange={(e) => onChange('token', e.target.value)}
            />
            {errors.token && <span className="text-red-500 text-xs mt-1">{errors.token}</span>}
          </div>
        )}

        {config.authType === 'headers' && (
          <div className="w-full">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col text-sm">
                <span className="text-[#344054] font-medium mb-1">{HeadersText}</span>
                {keyValuePairs?.map((pair, index) => (
                  <div key={pair.id} className="flex gap-2 mt-1">
                    <input
                      placeholder="Key"
                      className={`w-[41%] rounded-lg px-3 py-2 ${
                        fieldErrors.key[index] ? 'border-red-500' : 'border border-[#E4E4E7]'
                      }`}
                      value={pair.key}
                      onChange={(e) => updateKeyValuePair(index, 'key', e.target.value)}
                    />
                    <input
                      placeholder="Value"
                      className={`w-[41%] rounded-lg px-3 py-2 ${
                        fieldErrors.value[index] ? 'border-red-500' : 'border border-[#E4E4E7]'
                      }`}
                      value={pair.value}
                      onChange={(e) => updateKeyValuePair(index, 'value', e.target.value)}
                    />
                    <div className="flex gap-1 ml-auto">
                      {index === keyValuePairs.length - 1 && (
                        <button
                          className="p-2 hover:bg-gray-200 rounded-lg"
                          type="button"
                          onClick={addKeyValuePair}
                        >
                          <img src={Plus} alt="add" className="w-4 h-4" />
                        </button>
                      )}
                      {index === keyValuePairs.length - 1 && index > 0 && (
                        <button
                          className="p-2 hover:bg-red-200 rounded-lg"
                          type="button"
                          onClick={() => removeKeyValuePair(index)}
                        >
                          <img src={Trash} alt="delete" className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {errors.headers && (
                  <span className="text-red-500 text-xs mt-1">{errors.headers}</span>
                )}
              </div>
            </div>
          </div>
        )}

        {config.authType === 'oauth_pre_registered' && (
          <>
            <div className="flex flex-col text-sm">
              <span className="text-[#344054] font-medium">
                Client ID
                <span className="text-red-600">*</span>
              </span>
              <input
                placeholder="Enter OAuth client ID"
                className={`mt-1 rounded-lg ${
                  errors.clientId ? '!border-red-500 border' : 'border border-[#E4E4E7]'
                }`}
                value={config.clientId}
                onChange={(e) => onChange('clientId', e.target.value)}
              />
              {errors.clientId && (
                <span className="text-red-500 text-xs mt-1">{errors.clientId}</span>
              )}
            </div>
            <div className="flex flex-col text-sm">
              <span className="text-[#344054] font-medium">Client Secret</span>
              <input
                type="password"
                placeholder="Enter OAuth client secret (if required)"
                className="mt-1 rounded-lg border border-[#E4E4E7]"
                value={config.clientSecret}
                onChange={(e) => onChange('clientSecret', e.target.value)}
              />
            </div>
          </>
        )}
      </div>
    </ActionModal>
  );
}

export default TuneModal;
